/**
 * Click-once buttons and confirmation dialogs.
 *
 * Provides buttons that disable themselves after click to prevent double-actions
 * (re-enables after 15s), and "Sure? Yes/No" confirmation wrappers.
 *
 * - `ClickOnceButton` / `ClickOnceButton2` — Single-click protection (two style variants)
 * - `SureYesNoButton` / `SureYesNoButton2` — Confirmation dialog before action
 *
 * Used extensively in Sideways view for check-in, trace, and alarm actions.
 *
 * @module ClickOnceButton
 */

import React from 'react';

import { smallButtonStyle, smallButtonStyle2 } from './styles/constants';
import type {
  ClickOnceButtonBaseProps,
  ClickOnceButtonProps,
  SureYesNoButtonBaseProps,
  SureYesNoButtonProps,
} from './types';

/** Base click-once button — disables for 15s after click to prevent double-actions */
const ClickOnceButtonBase: React.FC<ClickOnceButtonBaseProps> = ({ style, text, onClick }) => {
  const [clicked, setClicked] = React.useState<boolean>(false);

  // Re-enable after 15 seconds - only when button is clicked
  React.useEffect(() => {
    if (!clicked) return; // Only set timer when button is actually clicked

    const timer = setTimeout(() => setClicked(false), 15000);
    return () => clearTimeout(timer);
  }, [clicked]); // Dependency on clicked state

  return (
    <button
      style={{ ...style, minWidth: '10vw' }}
      disabled={clicked}
      onClick={() => {
        setClicked(true);
        onClick();
      }}
    >
      {text}
    </button>
  );
};

export const ClickOnceButton: React.FC<ClickOnceButtonProps> = (props) => (
  <ClickOnceButtonBase {...props} style={smallButtonStyle} />
);
export const ClickOnceButton2: React.FC<ClickOnceButtonProps> = (props) => (
  <ClickOnceButtonBase {...props} style={smallButtonStyle2} />
);

const SureYesNoButtonBase: React.FC<SureYesNoButtonBaseProps> = ({
  ClickOnceButtonComponent,
  ...props
}) => {
  const [show, setShow] = React.useState<boolean>(props.open ? true : false);

  React.useEffect(() => {
    const timer = show ? setTimeout(() => setShow(false), 30 * 1000) : null;
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [show]);

  const onNo = props.onNo || ((): void => {});

  if (show) {
    return (
      <div>
        {props.noText ? null : <ClickOnceButtonComponent text={props.text} onClick={() => {}} />}
        <div
          style={{ position: 'fixed', left: 0, right: 0, top: 0, bottom: 0, background: '#0003' }}
          onClick={() => {
            onNo();
            setShow(false);
          }}
        ></div>
        <div
          style={{
            background: 'white',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'max(280px, 30vw)',
            borderRadius: '1.5vmin',
            boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
            border: '1px solid #ddd',
            padding: 'max(20px, 3vmin)',
            zIndex: 3000,
          }}
        >
          <div
            style={{
              fontSize: 'max(15px, 2.2vmin)',
              fontWeight: 'bold',
              color: '#333',
              marginBottom: 'max(12px, 2vmin)',
              textAlign: 'center',
            }}
          >
            Confirm {props.text}?
          </div>
          {props.info && (
            <div
              style={{
                fontSize: 'max(12px, 1.6vmin)',
                color: '#666',
                marginBottom: 'max(12px, 2vmin)',
                textAlign: 'center',
              }}
            >
              {props.info}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              gap: 'max(10px, 1.5vmin)',
              justifyContent: 'center',
            }}
          >
            <button
              style={{
                ...smallButtonStyle,
                flex: 1,
                padding: 'max(10px, 1.2vmin)',
                fontSize: 'max(14px, 2vmin)',
                borderRadius: '0.8vmin',
              }}
              onClick={(e) => {
                props.onClick();
                e.stopPropagation();
                setShow(false);
              }}
            >
              Yes
            </button>
            <button
              style={{
                ...smallButtonStyle,
                flex: 1,
                padding: 'max(10px, 1.2vmin)',
                fontSize: 'max(14px, 2vmin)',
                borderRadius: '0.8vmin',
              }}
              onClick={() => {
                onNo();
                setShow(false);
              }}
            >
              No
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <ClickOnceButtonComponent text={props.text} onClick={() => setShow(true)} />;
};

export const SureYesNoButton: React.FC<SureYesNoButtonProps> = (props) => (
  <SureYesNoButtonBase {...props} ClickOnceButtonComponent={ClickOnceButton} />
);
export const SureYesNoButton2: React.FC<SureYesNoButtonProps> = (props) => (
  <SureYesNoButtonBase {...props} ClickOnceButtonComponent={ClickOnceButton2} />
);
